import { Link, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { ArrowLeft, Calendar, Users, Mail, Phone, User, MessageSquare, Info } from 'lucide-react';
import { useCustomerBookingDetail } from '@/hooks/useCustomerBookingDetail';
import { BookingTimeline } from '@/components/booking/BookingTimeline';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { formatMoneyWhole } from '@/lib/formatMoney';

function fmtDate(dateStr: string) {
  try {
    return format(new Date(dateStr), 'd MMMM yyyy', { locale: sv });
  } catch {
    return dateStr;
  }
}

export default function CustomerBookingDetailPage() {
  const { bookingId } = useParams<{ bookingId: string }>();
  const { data: vm, isLoading, error } = useCustomerBookingDetail(bookingId);

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-2xl px-4 sm:px-6 pt-6 pb-24">
        <Skeleton className="h-5 w-24 mb-6" />
        <Skeleton className="h-8 w-64 mb-2" />
        <Skeleton className="h-5 w-48 mb-8" />
        <div className="space-y-4">
          <Skeleton className="h-32 w-full rounded-lg" />
          <Skeleton className="h-48 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  if (error || !vm) {
    return (
      <div className="mx-auto w-full max-w-2xl px-4 sm:px-6 pt-6 pb-24">
        <Button asChild variant="ghost" size="sm" className="text-muted-foreground mb-6">
          <Link to="/app/customer/bookings">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Tillbaka
          </Link>
        </Button>
        <Card className="border-destructive/50 bg-destructive/5 shadow-none">
          <CardContent className="p-8 text-center text-destructive">
            <p>{error instanceof Error ? error.message : 'Kunde inte ladda bokningen.'}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 pt-6 pb-24">
      {/* Back */}
      <Button asChild variant="ghost" size="sm" className="text-muted-foreground mb-6">
        <Link to="/app/customer/bookings">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Mina bokningar
        </Link>
      </Button>

      {/* Header – full width */}
      <header className="mb-6">
        <div className="flex items-center gap-3 flex-wrap mb-1">
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-foreground">
            {vm.title}
          </h1>
          <Badge variant={vm.statusVariant}>{vm.displayStatus}</Badge>
        </div>
        <p className="text-sm text-muted-foreground">{vm.dateRange}</p>
        <p className="text-sm text-muted-foreground mt-0.5">
          {vm.producerName} · {vm.guestCount} {vm.guestCount === 1 ? 'gäst' : 'gäster'}
        </p>
      </header>

      {/* Next step – full width */}
      {vm.nextStepText && (
        <div className="flex items-start gap-3 rounded-lg bg-muted/50 border border-border/40 p-4 mb-8">
          <Info className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
          <p className="text-sm text-foreground leading-relaxed">{vm.nextStepText}</p>
        </div>
      )}

      {/* Main + aside grid */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_20rem] gap-8 lg:gap-10">
        {/* ── Main column ── */}
        <div className="space-y-8">
          {/* Timeline */}
          <section>
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-4">
              Status
            </h2>
            <Card className="border-border/50 shadow-none">
              <CardContent className="p-5 sm:p-6">
                <BookingTimeline steps={vm.timelineSteps} />
              </CardContent>
            </Card>
          </section>

          {/* Booking details */}
          <section>
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-4">
              Bokningsdetaljer
            </h2>
            <Card className="border-border/50 shadow-none">
              <CardContent className="p-5 sm:p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <DetailRow icon={Calendar} label="Incheckning" value={fmtDate(vm.checkIn)} />
                  <DetailRow icon={Calendar} label="Utcheckning" value={fmtDate(vm.checkOut)} />
                  <DetailRow icon={Users} label="Antal gäster" value={`${vm.guestCount} ${vm.guestCount === 1 ? 'gäst' : 'gäster'}`} />
                  <DetailRow icon={Calendar} label="Bokad" value={fmtDate(vm.createdAt)} />
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Guest message */}
          {vm.guestMessage && (
            <section>
              <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-4">
                Ditt meddelande
              </h2>
              <Card className="border-border/50 shadow-none">
                <CardContent className="p-5 sm:p-6">
                  <div className="flex gap-3">
                    <MessageSquare className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                    <p className="text-sm text-foreground whitespace-pre-line">{vm.guestMessage}</p>
                  </div>
                </CardContent>
              </Card>
            </section>
          )}
        </div>

        {/* ── Side column ── */}
        <div className="space-y-6 lg:pt-0">
          {/* Payment */}
          <section>
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
              Betalning
            </h2>
            <Card className="border-border/50 shadow-none">
              <CardContent className="p-4 sm:p-5">
                <p className="text-sm font-medium text-foreground">{vm.paymentSummary.label}</p>
                {vm.paymentSummary.remainingLabel && (
                  <p className="text-sm text-muted-foreground mt-1">{vm.paymentSummary.remainingLabel}</p>
                )}
              </CardContent>
            </Card>
          </section>

          {/* Guest info */}
          <section>
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
              Dina uppgifter
            </h2>
            <Card className="border-border/50 shadow-none">
              <CardContent className="p-4 sm:p-5 space-y-3">
                {vm.guestName && <DetailRow icon={User} label="Namn" value={vm.guestName} />}
                <DetailRow icon={Mail} label="E-post" value={vm.guestEmail} />
                {vm.guestPhone && <DetailRow icon={Phone} label="Telefon" value={vm.guestPhone} />}
              </CardContent>
            </Card>
          </section>
        </div>
      </div>

      {/* Bottom CTAs – full width */}
      <div className="flex flex-col sm:flex-row gap-3 pt-8 mt-4">
        <Button asChild variant="outline" size="sm">
          <Link to="/app/customer/bookings">Tillbaka till mina bokningar</Link>
        </Button>
        <Button asChild variant="ghost" size="sm">
          <Link to="/explore?tab=places">Utforska fler platser</Link>
        </Button>
      </div>
    </div>
  );
}

function DetailRow({ icon: Icon, label, value }: { icon: React.ComponentType<{ className?: string }>; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm text-foreground">{value}</p>
      </div>
    </div>
  );
}
